import moment, { Moment } from 'moment'
import React, { useState } from 'react'
import 'react-calendar-timeline/lib/Timeline.css'
import Timeline, {
  SidebarHeader,
  TimelineHeaders,
  DateHeader,
  TimelineGroupBase,
  TimelineItemBase,
  TodayMarker,
} from 'react-calendar-timeline'
import { ABSENCE_REASONS } from 'const/absenceReasons'
import styles from './styles.module.scss'

export type AbsenceView = 'me' | 'everyone'

interface IProps {
  users: TimelineGroupBase[]
  absences: TimelineItemBase<Moment>[]
  view: AbsenceView
  onViewChange: (view: AbsenceView) => void
  canDelete: (absenceId: TimelineItemBase<Moment>['id']) => boolean
  isAdmin: boolean
  setDeleteModal: (open: boolean) => void
  setDeleteData: (data: any) => void
}

const VIEWS: { key: AbsenceView; label: string }[] = [
  { key: 'me', label: 'Me' },
  { key: 'everyone', label: 'Everyone' },
]

const ZOOM_LEVELS = [
  { key: 'week', label: 'Week', days: 7 },
  { key: 'twoWeeks', label: '2 weeks', days: 14 },
  { key: 'month', label: 'Month', days: 31 },
]

const rangeFor = (start: Moment, days: number) => ({
  start: start.clone().startOf('day'),
  end: start.clone().startOf('day').add(days, 'days'),
})

export const CustomTimeline: React.FC<IProps> = (props: IProps) => {
  const [zoomDays, setZoomDays] = useState(14)
  const [range, setRange] = useState(() => rangeFor(moment().startOf('isoWeek'), 14))

  const shift = (direction: 1 | -1) =>
    setRange(rangeFor(range.start.clone().add(direction * zoomDays, 'days'), zoomDays))

  const setZoom = (days: number) => {
    setZoomDays(days)
    setRange(rangeFor(range.start, days))
  }

  function openDeleteModal(itemId: TimelineItemBase<Moment>['id']) {
    if (!props.canDelete(itemId)) {
      return
    }
    props.setDeleteModal(true)
    props.setDeleteData({ id: itemId })
  }

  const rangeLabel = `${range.start.format('DD.MM.')} – ${range.end
    .clone()
    .subtract(1, 'day')
    .format('DD.MM.YYYY.')}`

  return (
    <div className={styles.card}>
      <div className={styles.toolbar}>
        <div className={styles.navigation}>
          <button
            type="button"
            className={styles.navButton}
            aria-label="Previous period"
            onClick={() => shift(-1)}
          >
            ‹
          </button>
          <button
            type="button"
            className={styles.todayButton}
            onClick={() => setRange(rangeFor(moment().startOf('isoWeek'), zoomDays))}
          >
            Today
          </button>
          <button
            type="button"
            className={styles.navButton}
            aria-label="Next period"
            onClick={() => shift(1)}
          >
            ›
          </button>
          <span className={styles.rangeLabel}>{rangeLabel}</span>
        </div>

        <div className={styles.toolbarControls}>
          <div className={styles.zoom} role="group" aria-label="Show absences for">
            {VIEWS.map((view) => (
              <button
                type="button"
                key={view.key}
                aria-pressed={props.view === view.key}
                className={`${styles.zoomButton} ${
                  props.view === view.key ? styles.zoomActive : ''
                }`}
                onClick={() => props.onViewChange(view.key)}
              >
                {view.label}
              </button>
            ))}
          </div>

          <div className={styles.zoom} role="group" aria-label="Zoom">
            {ZOOM_LEVELS.map((zoom) => (
              <button
                type="button"
                key={zoom.key}
                className={`${styles.zoomButton} ${
                  zoomDays === zoom.days ? styles.zoomActive : ''
                }`}
                onClick={() => setZoom(zoom.days)}
              >
                {zoom.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.legend}>
        {Object.values(ABSENCE_REASONS).map((reason) => (
          <span className={styles.legendItem} key={reason.label}>
            <span className={styles.legendSwatch} style={{ backgroundColor: reason.color }} />
            {reason.label}
          </span>
        ))}
        <span className={styles.hint}>
          {props.isAdmin ? 'Click an absence to delete it' : 'Click your absence to delete it'}
        </span>
      </div>

      <div className={styles.timeline}>
        <Timeline
          groups={props.users}
          items={props.absences}
          stackItems
          canMove={false}
          canChangeGroup={false}
          canResize={false}
          itemTouchSendsClick={false}
          sidebarWidth={200}
          lineHeight={44}
          itemHeightRatio={0.7}
          visibleTimeStart={range.start.valueOf()}
          visibleTimeEnd={range.end.valueOf()}
          onTimeChange={(visibleTimeStart, visibleTimeEnd, updateScrollCanvas) => {
            setRange({ start: moment(visibleTimeStart), end: moment(visibleTimeEnd) })
            updateScrollCanvas(visibleTimeStart, visibleTimeEnd)
          }}
          // The library fires onItemSelect on the first click and onItemClick only once the
          // item is already selected; handle both so a single click opens the delete dialog
          onItemSelect={openDeleteModal}
          onItemClick={openDeleteModal}
        >
          <TimelineHeaders>
            <SidebarHeader>
              {({ getRootProps }) => (
                <div {...getRootProps()} className={styles.sidebarHeader}>
                  Employee
                </div>
              )}
            </SidebarHeader>
            <DateHeader unit="primaryHeader" />
            <DateHeader
              labelFormat={([startTime], unit, labelWidth) =>
                labelWidth > 60 ? startTime.format('ddd DD') : startTime.format('DD')
              }
            />
          </TimelineHeaders>
          <TodayMarker date={moment().toDate()}>
            {({ styles: markerStyles }) => (
              <div style={{ ...markerStyles, backgroundColor: '#e05d5d', width: '2px' }} />
            )}
          </TodayMarker>
        </Timeline>
      </div>
    </div>
  )
}

export default CustomTimeline
